import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { mainNavLinks, storeBarLinks, utilityLinks } from "@/data/navigation";
import { homePath, searchPath } from "@/lib/routes";

function IconSearch() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  );
}
function IconMapPin() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
function IconBag() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}
function IconHeart() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
function IconUser() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function IconMenu() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}
function IconX() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function SiteHeader() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (q) {
      navigate(searchPath(q));
      setQuery("");
      searchInputRef.current?.blur();
    }
  }

  return (
    <>
      {/* Skip to main content — accessibility */}
      <a href="#main-content" className="kh-skip-link">
        Skip to main content
      </a>

      <header
        id="site-header"
        className={`kh-header${scrolled ? " kh-header--scrolled" : ""}`}
      >
        {/* ── Utility bar ── */}
        <div id="header-utility" className="kh-utility">
          <div className="kh-utility__inner kh-container">
            <span className="kh-utility__store">
              <IconMapPin />
              Shop at <strong>Chadstone</strong>
            </span>
            <nav aria-label="Utility links" className="kh-utility__links">
              {utilityLinks.map((link) => (
                <a key={link.label} href={link.href} className="kh-utility__link">
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
        </div>

        {/* ── Main header row ── */}
        <div className="kh-main">
          <div className="kh-main__inner kh-container">
            {/* Mobile hamburger */}
            <button
              type="button"
              className="kh-hamburger"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="header-nav"
              onClick={() => setMobileMenuOpen((v) => !v)}
            >
              {mobileMenuOpen ? <IconX /> : <IconMenu />}
            </button>

            {/* Logo */}
            <Link to={homePath()} id="header-logo" className="kh-logo" aria-label="Kmart home">
              Kmart
            </Link>

            {/* Search */}
            <form
              id="header-search-form"
              className={`kh-search${searchFocused ? " kh-search--focused" : ""}`}
              role="search"
              onSubmit={handleSearch}
            >
              <label htmlFor="header-search" className="kh-sr-only">
                Search products
              </label>
              <span className="kh-search__icon" aria-hidden="true">
                <IconSearch />
              </span>
              <input
                ref={searchInputRef}
                id="header-search"
                type="search"
                className="kh-search__input"
                placeholder="Find Products, categories & more"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                autoComplete="off"
              />
              <button
                type="submit"
                className="kh-search__btn"
                aria-label="Submit search"
                disabled={!query.trim()}
              >
                Search
              </button>
            </form>

            {/* Action buttons */}
            <div id="header-actions" className="kh-actions">
              <button type="button" className="kh-action" id="header-stores" aria-label="Find a store">
                <IconMapPin />
                <span className="kh-action__label">Stores</span>
              </button>
              <button type="button" className="kh-action kh-action--badge" id="header-bag" aria-label="Shopping bag, 0 items">
                <span className="kh-action__icon-wrap">
                  <IconBag />
                  <span className="kh-badge" aria-hidden="true">0</span>
                </span>
                <span className="kh-action__label">Bag</span>
              </button>
              <button type="button" className="kh-action" id="header-wishlist" aria-label="Wishlist">
                <IconHeart />
                <span className="kh-action__label">Wishlist</span>
              </button>
              <button type="button" className="kh-action" id="header-signin" aria-label="Sign in or sign up">
                <IconUser />
                <span className="kh-action__label">Sign In/Up</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── Secondary links (Track order, Flybuys…) ── */}
        <div id="store-locator-bar" className="kh-secondary">
          <div className="kh-secondary__inner kh-container">
            {storeBarLinks.map((link) => (
              <a key={link.label} href={link.href} className="kh-secondary__link">
                {link.label}
              </a>
            ))}
          </div>
        </div>

        {/* ── Primary nav ── */}
        <nav
          id="header-nav"
          className={`kh-nav${mobileMenuOpen ? " kh-nav--open" : ""}`}
          aria-label="Primary navigation"
        >
          <div className="kh-nav__inner kh-container">
            {mainNavLinks.map((link) => (
              <NavLink
                key={link.label}
                to={link.href}
                className={({ isActive }) =>
                  `kh-nav__link${isActive ? " kh-nav__link--active" : ""}`
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </header>
    </>
  );
}
